import { getCurrencySymbol } from '@angular/common';
import { Component, computed, inject, signal, input } from '@angular/core';
import { ThemeService } from '../services/theme';
import { DataSources } from '../data-sources';
import { EarningsStatDataPoint, EarningsStatDataSet, EarningsStats } from '../earnings-data';

export type { EarningsStatDataPoint, EarningsStatDataSet, EarningsStats };

interface ChartBar {
  label: string;
  reported: number | null;
  expected: number | null;
}

// V3's dual-bar-per-period model — Gewinn and Umsatz shown together, not metric-toggled.
interface V3ChartBar {
  label: string;
  gewinn: number | null;
  gewinnEstimate: boolean;
  umsatz: number | null;
  umsatzEstimate: boolean;
}

@Component({
  selector: 'app-revenue-chart',
  imports: [],
  standalone: true,
  templateUrl: './revenue-chart.html',
  styleUrl: './revenue-chart.scss',
  // Only the V3 Earnings components set [v3]; EarningsAnalysisV2 never does, so its V2 chart is unaffected.
  host: { '[class.v3]': 'v3()' },
})
export class RevenueChart {
  readonly earningsStats = input<EarningsStats | null>(null);
  readonly v3 = input(false);

  private readonly themeService = inject(ThemeService);

  readonly dataSources = DataSources;

  readonly metricTabs = ['Umsatz', 'Gewinn'];
  readonly periodOptions = ['Jährlich', 'Quartalsweise'];

  readonly activeMetric = signal(0);
  readonly activePeriod = signal(0);
  readonly dropdownOpen = signal(false);

  get chevronPath(): string {
    return this.themeService.isLight() ? '/chevron-down.svg' : '/chevron-down-light.svg';
  }

  private formatLabel(label: string | number): string {
    if (typeof label === 'number') {
      return `GJ ${String(label).slice(-2)}`;
    }
    const match = label.match(/^(Q\d)\s(\d{4})$/);
    if (match) {
      return `${match[1]} '${match[2].slice(-2)}`;
    }
    return label;
  }

  private mapToChartBars(points: EarningsStatDataPoint[]): ChartBar[] {
    return points.map((p) => ({
      label: this.formatLabel(p.label),
      reported: p.reported,
      expected: p.expected,
    }));
  }

  private readonly activePoints = computed((): EarningsStatDataPoint[] => {
    const stats = this.earningsStats();
    if (!stats) return [];
    const dataset = this.activeMetric() === 0 ? stats.revenue_data : stats.profit_data;
    return this.activePeriod() === 0 ? dataset.yearly : dataset.quarterly;
  });

  readonly bars = computed((): ChartBar[] => this.mapToChartBars(this.activePoints()));

  // V3: both metrics per period — no metric toggle, just the active period.
  readonly v3Bars = computed((): V3ChartBar[] => {
    const stats = this.earningsStats();
    if (!stats) return [];
    const key = this.activePeriod() === 0 ? 'yearly' : 'quarterly';
    const profit = stats.profit_data[key];
    const revenue = stats.revenue_data[key];
    const length = Math.max(profit.length, revenue.length);
    return Array.from({ length }, (_, i) => {
      const p = profit[i];
      const r = revenue[i];
      return {
        label: this.formatLabel((p ?? r).label),
        gewinn: p ? (p.reported ?? p.expected) : null,
        gewinnEstimate: p ? p.reported === null : false,
        umsatz: r ? (r.reported ?? r.expected) : null,
        umsatzEstimate: r ? r.reported === null : false,
      };
    });
  });

  private readonly activeValues = computed((): EarningsStatDataPoint[] => {
    if (!this.v3()) return this.activePoints();
    const stats = this.earningsStats();
    if (!stats) return [];
    const key = this.activePeriod() === 0 ? 'yearly' : 'quarterly';
    return [...stats.revenue_data[key], ...stats.profit_data[key]];
  });

  readonly currency = computed((): string | undefined => {
    const fromActive = this.activeValues().find((p) => p.currency)?.currency;
    if (fromActive) return fromActive;
    const stats = this.earningsStats();
    if (!stats) return undefined;
    // Currency is uniform across a stock — fall back to any dataset that carries it
    return [
      ...stats.revenue_data.yearly,
      ...stats.revenue_data.quarterly,
      ...stats.profit_data.yearly,
      ...stats.profit_data.quarterly,
    ].find((p) => p.currency)?.currency;
  });

  // Feeds the shared scale below — V3's two metrics per period, or V2's single active metric.
  private readonly allValues = computed((): number[] => {
    if (this.v3()) {
      return this.v3Bars().flatMap((b) =>
        [b.gewinn, b.umsatz].filter((v): v is number => v !== null),
      );
    }
    return this.bars().flatMap((b) =>
      [b.reported, b.expected].filter((v): v is number => v !== null),
    );
  });

  // Full tick set that defines the coordinate system — never filtered
  private readonly scaleTicks = computed((): number[] => {
    const hasData = this.v3() ? this.v3Bars().length > 0 : this.bars().length > 0;
    if (!hasData) return [];

    const allVals = this.allValues();

    const maxVal = Math.max(...allVals.filter((v) => v > 0), 0);
    const minVal = Math.min(...allVals.filter((v) => v < 0), 0);

    const ticks: number[] = [];

    if (maxVal > 0) {
      const step = this.niceStep(maxVal / 3);
      // V3: forces exactly 3 positive ticks; niceStep rounds up, so step * 3 >= maxVal always.
      const top = this.v3() ? step * 3 : Math.ceil(maxVal / step) * step;
      for (let v = top; v > 0; v -= step) {
        ticks.push(v);
      }
    }

    ticks.push(0);

    if (minVal < 0) {
      const step = this.niceStep(Math.abs(minVal) / 2);
      ticks.push(Math.floor(minVal / step) * step);
    }

    return ticks;
  });

  // Filtered tick set for axis labels only — drops ticks that are too close together.
  // Scale/position calculations always use scaleTicks so no bars fall outside the chart area.
  readonly yTicks = computed((): number[] => {
    const raw = this.scaleTicks();
    const range = Math.max(...raw) - Math.min(...raw);
    if (range === 0) return raw;
    return raw.filter((tick, i) => {
      if (i === 0) return true;
      return Math.abs(raw[i - 1] - tick) / range >= 0.1;
    });
  });

  readonly yMax = computed(() => {
    const ticks = this.scaleTicks();
    return ticks.length > 0 ? Math.max(...ticks) : 0;
  });

  readonly yMin = computed(() => {
    const ticks = this.scaleTicks();
    return ticks.length > 0 ? Math.min(...ticks) : 0;
  });

  readonly range = computed(() => this.yMax() - this.yMin());

  // Percentage from top where the zero baseline sits
  readonly zeroTopPercent = computed(() => {
    const r = this.range();
    return r > 0 ? (this.yMax() / r) * 100 : 100;
  });

  // Percentage from bottom where the zero baseline sits (for bottom-anchored positive bars)
  zeroBottomPercent(): number {
    return 100 - this.zeroTopPercent();
  }

  // Top offset (%) for a tick label — centers it on its grid line
  tickTopPercent(value: number): number {
    const r = this.range();
    if (r === 0) return 0;
    return ((this.yMax() - value) / r) * 100;
  }

  // Height of a bar as a percentage of the full range
  barAbsHeight(value: number): number {
    const r = this.range();
    if (r === 0) return 0;
    return (Math.abs(value) / r) * 100;
  }

  // Values from backend are in millions; display as Mrd when >= 1000, else Mio.
  // Appends currency symbol/code when provided (e.g. "12,3 Mrd $", "500 Mio €", "0 $").
  formatTick(value: number, currency?: string): string {
    const suffix = currency ? ` ${getCurrencySymbol(currency, 'narrow')}` : '';
    if (value === 0) return `0${suffix}`;
    const abs = Math.abs(value);
    const formatted =
      abs >= 1000
        ? (abs / 1000).toFixed(1).replace('.', ',') + ` Mrd${suffix}`
        : abs.toFixed(0) + ` Mio${suffix}`;
    return value < 0 ? `-${formatted}` : formatted;
  }

  selectMetric(index: number) {
    this.activeMetric.set(index);
  }

  selectPeriod(index: number) {
    this.activePeriod.set(index);
    this.dropdownOpen.set(false);
  }

  toggleDropdown() {
    this.dropdownOpen.update((v) => !v);
  }

  private niceStep(raw: number): number {
    if (raw <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const norm = raw / mag;
    if (norm <= 1) return mag;
    if (norm <= 2) return 2 * mag;
    if (norm <= 5) return 5 * mag;
    return 10 * mag;
  }
}
